import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { emailBroadcastService } from '@/_services/api/emailIntegration/emailBroadcast';
import EmailBroadcastComposer from './EmailBroadcastComposer';
import EmailBroadcastDraftList from './EmailBroadcastDraftList';
import { GoToTeamSettings } from '@/_elements/Links';

const EmailBroadcastContainer = ({ teamId, onClose }) => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [composerKey, setComposerKey] = useState(0);

    const fetchDrafts = async () => {
        if (!teamId) return;
        setLoading(true);
        try {
            const res = await emailBroadcastService.getDrafts({ team: teamId, limit: 50 });
            setDrafts(res?.results || []);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            toast.error(error?.message || 'Failed to load broadcast drafts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrafts();
    }, [teamId]);

    const handleNew = () => {
        setSelectedDraft(null);
        setComposerKey((prev) => prev + 1);
        setComposerOpen(true);
    };

    const handleOpen = (draft) => {
        setSelectedDraft(draft);
        setComposerKey((prev) => prev + 1);
        setComposerOpen(true);
    };

    const handleDelete = async (draft) => {
        const result = await Swal.fire({
            title: 'Delete broadcast draft?',
            text: `Delete "${draft?.subject || '(no subject)'}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
        });
        if (!result.isConfirmed) return;
        try {
            await emailBroadcastService.deleteDraft(draft?._id || draft?.id);
            setDrafts((prev) => prev.filter((d) => String(d._id || d.id) !== String(draft?._id || draft?.id)));
            toast.success('Broadcast draft deleted');
        } catch (err) {
            toast.error(err?.message || 'Failed to delete draft');
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-end mb-3">
                <Button variant="outline-secondary" size="sm" onClick={fetchDrafts} disabled={loading} className="me-2">
                    <FiRefreshCw className={loading ? 'spin' : ''} />
                </Button>
                <Button variant="primary" size="sm" onClick={handleNew} disabled={loading}>
                    <FiPlus className="me-2" />
                    Broadcast
                </Button>
                <GoToTeamSettings
                    showLink={true}
                    team={{
                        _id: teamId,
                    }}
                    tab="email"
                />
            </div>

            <EmailBroadcastDraftList items={drafts} onOpen={handleOpen} onDelete={handleDelete} loading={loading} />
            <EmailBroadcastComposer
                key={composerKey}
                show={composerOpen}
                teamId={teamId}
                draftId={selectedDraft?._id}
                existingDraft={selectedDraft}
                onClose={() => {
                    setComposerOpen(false);
                    fetchDrafts();
                }}
                onDraftSaved={fetchDrafts}
                onBroadcastSent={() => {}}
            />
        </div>
    );
};

export default EmailBroadcastContainer;
